import dayjs from 'dayjs';

export const MGB_PLANNED_LINK_UNSET = Symbol('mgb-planned-unset');

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

// Empty / placeholder values used by MGB stop datetime fields.
export function isEmptyMGBDateValue(value: unknown): boolean {
  if (value == null) {
    return true;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime());
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  return false;
}

export function isMGBDateOnlyValue(value: unknown): boolean {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (DATE_ONLY_RE.test(trimmed)) {
      return true;
    }

    const day = dayjs(trimmed);
    if (!day.isValid()) {
      return false;
    }
    return isLocalMidnightDayjs(day);
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return false;
    }
    return (
      value.getHours() === 0 &&
      value.getMinutes() === 0 &&
      value.getSeconds() === 0 &&
      value.getMilliseconds() === 0
    );
  }
  return false;
}

function isLocalMidnightDayjs(day: dayjs.Dayjs): boolean {
  return (
    day.hour() === 0 &&
    day.minute() === 0 &&
    day.second() === 0 &&
    day.millisecond() === 0
  );
}

/** Parse planned values as wall-clock (date-only strings → local midnight). */
export function parseMGBPlannedDate(value: unknown): dayjs.Dayjs | null {
  if (isEmptyMGBDateValue(value)) {
    return null;
  }

  if (typeof value === 'string' && DATE_ONLY_RE.test(value.trim())) {
    const day = dayjs(`${value.trim()}T00:00:00`);
    return day.isValid() ? day : null;
  }
  const day = dayjs(value as string | Date);
  return day.isValid() ? day : null;
}

/**
 * Stable wall-clock key so Date midnight and `YYYY-MM-DD` for the same local
 * day compare equal (avoid UTC `toISOString()` mismatches).
 */
export function plannedDateLinkKey(
  value: unknown
): string | typeof MGB_PLANNED_LINK_UNSET {
  if (value === MGB_PLANNED_LINK_UNSET) {
    return MGB_PLANNED_LINK_UNSET;
  }
  const day = parseMGBPlannedDate(value);
  if (!day) {
    return '';
  }
  return day.format('YYYY-MM-DDTHH:mm:ss.SSS');
}

export function shouldMirrorMGBPlannedDate(args: {
  sourceChanged: boolean;
  sourceValue: unknown;
  targetValue: unknown;
}): boolean {
  const { sourceChanged, sourceValue, targetValue } = args;
  if (sourceChanged === false || isEmptyMGBDateValue(sourceValue)) {
    return false;
  }
  return isEmptyMGBDateValue(targetValue);
}

// True when end is strictly before start (equal is allowed).
export function isMGBPlannedEndBeforeStart(
  start: unknown,
  end: unknown
): boolean {
  if (isEmptyMGBDateValue(start) || isEmptyMGBDateValue(end)) {
    return false;
  }
  const startDay = parseMGBPlannedDate(start);
  const endDay = parseMGBPlannedDate(end);
  if (!startDay || !endDay) {
    return false;
  }
  if (isMGBDateOnlyValue(start) || isMGBDateOnlyValue(end)) {
    return endDay.startOf('day').isBefore(startDay.startOf('day'));
  }
  return endDay.isBefore(startDay);
}

export const MGB_PLANNED_END_BEFORE_START_MESSAGE =
  'Planned End cannot be before Planned Start';
